import { useState } from 'react';
import { detectSocialPlatform } from '@/utils/socialPlatformDetection';

export function useSocialLinks(initial: string[] = ['']) {
  const [links, setLinks] = useState<string[]>(initial);
  const [saved, setSaved] = useState<Set<number>>(new Set());
  const [original, setOriginal] = useState<string[]>(initial);
  const [icons, setIcons] = useState<Record<number, string>>({});

  const add = () => {
    setLinks(prev => [...prev, '']);
    setOriginal(prev => [...prev, '']);
    setIcons(prev => ({ ...prev }));
  };

  const update = (index: number, value: string) => {
    const newLinks = [...links];
    newLinks[index] = value;
    setLinks(newLinks);

    if (value === '') {
      const newIcons = { ...icons };
      delete newIcons[index];
      setIcons(newIcons);
    } else {
      const platform = detectSocialPlatform(value);
      if (platform) {
        setIcons(prev => ({ ...prev, [index]: platform.iconComponent }));
      }
    }

    if (value === '' && saved.has(index)) {
      const newSaved = new Set(saved);
      newSaved.delete(index);
      setSaved(newSaved);

      if (index > 0) {
        const filteredLinks = newLinks.filter((_, i) => i !== index);
        setLinks(filteredLinks);

        const filteredOriginal = original.filter((_, i) => i !== index);
        setOriginal(filteredOriginal);

        const reindexedIcons: Record<number, string> = {};
        Object.keys(icons).forEach(key => {
          const k = parseInt(key);
          if (k < index) reindexedIcons[k] = icons[k];
          else if (k > index) reindexedIcons[k - 1] = icons[k];
        });
        setIcons(reindexedIcons);
      }
    }
  };

  const remove = (index: number) => {
    if (links.length <= 1) return;
    setLinks(prev => prev.filter((_, i) => i !== index));
    setOriginal(prev => prev.filter((_, i) => i !== index));
    const newSaved = new Set(saved);
    newSaved.delete(index);
    setSaved(newSaved);
    const reindexedIcons: Record<number, string> = {};
    Object.keys(icons).forEach(key => {
      const k = parseInt(key);
      if (k < index) reindexedIcons[k] = icons[k];
      else if (k > index) reindexedIcons[k - 1] = icons[k];
    });
    setIcons(reindexedIcons);
  };

  const save = (index: number) => {
    const originalLink = links[index];
    const newOriginal = [...original];
    newOriginal[index] = originalLink;
    setOriginal(newOriginal);
    const newSaved = new Set(saved);
    newSaved.add(index);
    setSaved(newSaved);
    const platform = detectSocialPlatform(links[index]);
    if (platform)
      setIcons(prev => ({ ...prev, [index]: platform.iconComponent }));
  };

  return { links, saved, original, icons, add, update, remove, save } as const;
}
